// src/components/Layout/Navbar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar as BsNavbar, Nav, Container, Button, Image } from 'react-bootstrap';
import logo from '../../assets/images/cac-logo.jpeg';

const Navigation = () => {
    const { user, logout, isAuthenticated, isSuperAdmin, isAdmin, isOperator, isClient } = useAuth();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [logoError, setLogoError] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const getDashboardLink = () => {
        if (isSuperAdmin) return '/superadmin';
        if (isAdmin) return '/admin';
        if (isOperator) return '/operator';
        return '/client';
    };

    const handleLogout = () => {
        logout();
        navigate('/');
        setExpanded(false);
    };

    const handleLogoError = () => {
        setLogoError(true);
    };

    return (
        <BsNavbar
            expand="lg"
            expanded={expanded}
            className={`navbar-custom ${scrolled ? 'navbar-scrolled' : ''} py-3`}
            variant="dark"
        >
            <Container>
                {/* Logo avec fallback */}
                <BsNavbar.Brand as={Link} to="/" onClick={() => setExpanded(false)} className="brand-container">
                    <div className="logo-wrapper">
                        {!logoError ? (
                            <Image
                                src={logo}
                                alt="CAC Logo"
                                className="navbar-logo"
                                onError={handleLogoError}
                            />
                        ) : (
                            <div className="logo-fallback">
                                <span>🚚</span>
                            </div>
                        )}
                    </div>
                    <div className="brand-text">
                        <strong className="brand-name">CAC</strong>
                        <span className="brand-subtitle">Chine Afrique Cargo</span>
                    </div>
                </BsNavbar.Brand>

                {/* Toggle Button */}
                <BsNavbar.Toggle
                    onClick={() => setExpanded(!expanded)}
                    className="custom-toggler"
                >
                    <span className="toggler-icon"></span>
                    <span className="toggler-icon"></span>
                    <span className="toggler-icon"></span>
                </BsNavbar.Toggle>

                {/* Navigation Links */}
                <BsNavbar.Collapse id="basic-navbar-nav">
                    <Nav className="ms-auto align-items-center gap-2">

                        {/* Liens publics - pour non connecté OU client */}
                        {(!isAuthenticated || isClient) && (
                            <div className="nav-links-wrapper">
                                <Nav.Link as={Link} to="/" onClick={() => setExpanded(false)} className="nav-link-custom">
                                    <span className="nav-icon"></span>
                                    Accueil
                                </Nav.Link>
                                <Nav.Link as={Link} to="/services" onClick={() => setExpanded(false)} className="nav-link-custom">
                                    <span className="nav-icon"></span>
                                    Services
                                </Nav.Link>
                                <Nav.Link as={Link} to="/about" onClick={() => setExpanded(false)} className="nav-link-custom">
                                    <span className="nav-icon"></span>
                                    À propos
                                </Nav.Link>
                                <Nav.Link as={Link} to="/contact" onClick={() => setExpanded(false)} className="nav-link-custom">
                                    <span className="nav-icon"></span>
                                    Contact
                                </Nav.Link>
                                <Nav.Link as={Link} to="/tracking" onClick={() => setExpanded(false)} className="nav-link-custom">
                                    <span className="nav-icon"></span>
                                    Suivi
                                </Nav.Link>
                                <Nav.Link as={Link} to="/store" onClick={() => setExpanded(false)} className="nav-link-custom store-link">
                                    <span className="nav-icon">🛍️</span>
                                    CAC Store
                                </Nav.Link>
                            </div>
                        )}

                        {/* Utilisateur connecté */}
                        {isAuthenticated ? (
                            <div className="auth-links-wrapper">
                                <Nav.Link as={Link} to={getDashboardLink()} onClick={() => setExpanded(false)} className="nav-link-custom dashboard-link">
                                    <span className="nav-icon">📊</span>
                                    Dashboard
                                </Nav.Link>
                                {isClient && (
                                    <Nav.Link as={Link} to="/client/profile" onClick={() => setExpanded(false)} className="nav-link-custom">
                                        <span className="nav-icon">👤</span>
                                        Mon profil
                                    </Nav.Link>
                                )}
                                <Button
                                    variant="warning"
                                    size="sm"
                                    onClick={handleLogout}
                                    className="logout-btn"
                                >
                                    <span className="btn-icon">🚪</span>
                                    Déconnexion
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="warning"
                                size="sm"
                                as={Link}
                                to="/login"
                                onClick={() => setExpanded(false)}
                                className="login-btn"
                            >
                                <span className="btn-icon">🔐</span>
                                Connexion
                            </Button>
                        )}
                    </Nav>
                </BsNavbar.Collapse>
            </Container>

            <style>{`
                /* Base Navbar Styles */
                .navbar-custom {
                    background: linear-gradient(135deg, #0a0a2a 0%, #1a1a4e 50%, #0a0a2a 100%);
                    transition: all 0.3s ease;
                    position: sticky;
                    top: 0;
                    z-index: 1000;
                }
                
                .navbar-custom.navbar-scrolled {
                    padding: 8px 0;
                    background: linear-gradient(135deg, rgba(10, 10, 42, 0.95) 0%, rgba(26, 26, 78, 0.95) 50%, rgba(10, 10, 42, 0.95) 100%);
                    backdrop-filter: blur(10px);
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
                }
                
                /* Brand Styles */
                .brand-container {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    transition: transform 0.3s ease;
                }
                
                .brand-container:hover {
                    transform: scale(1.02);
                }
                
                .logo-wrapper {
                    position: relative;
                    width: 45px;
                    height: 45px;
                }
                
                .navbar-logo {
                    width: 45px;
                    height: 45px;
                    object-fit: contain;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }
                
                .logo-fallback {
                    width: 45px;
                    height: 45px;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    transition: all 0.3s ease;
                }
                
                .brand-container:hover .logo-fallback {
                    transform: rotate(5deg);
                }
                
                .brand-text {
                    display: flex;
                    flex-direction: column;
                }
                
                .brand-name {
                    font-size: 24px;
                    font-weight: 800;
                    line-height: 1.2;
                    background: linear-gradient(135deg, #fff 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                
                .brand-subtitle {
                    font-size: 10px;
                    color: rgba(255, 255, 255, 0.6);
                    letter-spacing: 1px;
                }
                
                /* Custom Toggler */
                .custom-toggler {
                    border: none;
                    padding: 8px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 8px;
                    transition: all 0.3s ease;
                }
                
                .custom-toggler:hover {
                    background: rgba(255, 255, 255, 0.2);
                }
                
                .toggler-icon {
                    display: block;
                    width: 22px;
                    height: 2px;
                    background: white;
                    margin: 5px 0;
                    transition: all 0.3s ease;
                }
                
                /* Navigation Links */
                .nav-links-wrapper, .auth-links-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                
                .nav-link-custom {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 18px !important;
                    border-radius: 40px;
                    font-weight: 500;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                
                .nav-link-custom::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
                    transition: left 0.5s ease;
                }
                
                .nav-link-custom:hover::before {
                    left: 100%;
                }
                
                .nav-icon {
                    font-size: 16px;
                    transition: transform 0.3s ease;
                }
                
                .nav-link-custom:hover .nav-icon {
                    transform: translateY(-2px);
                }
                
                .nav-link-custom:hover {
                    color: #F9A825 !important;
                    background: rgba(249, 168, 37, 0.1);
                    transform: translateY(-2px);
                }
                
                .dashboard-link {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    color: white !important;
                }
                
                .dashboard-link:hover {
                    background: linear-gradient(135deg, #B71C1C 0%, #F57F17 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(198, 40, 40, 0.3);
                }
                
                .store-link {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                
                /* Buttons */
                .logout-btn, .login-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 20px !important;
                    border-radius: 40px !important;
                    font-weight: 600;
                    font-size: 14px;
                    transition: all 0.3s ease;
                }
                
                .logout-btn {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                }
                
                .logout-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(198, 40, 40, 0.4);
                }
                
                .login-btn {
                    background: linear-gradient(135deg, #F9A825 0%, #C62828 100%);
                    border: none;
                    color: #212121;
                }
                
                .login-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(249, 168, 37, 0.4);
                }
                
                .btn-icon {
                    font-size: 14px;
                }
                
                /* Responsive */
                @media (max-width: 992px) {
                    .nav-links-wrapper, .auth-links-wrapper {
                        flex-direction: column;
                        width: 100%;
                        margin-top: 16px;
                    }
                    
                    .nav-link-custom {
                        width: 100%;
                        justify-content: center;
                        padding: 12px !important;
                    }
                    
                    .logout-btn, .login-btn {
                        width: 100%;
                        justify-content: center;
                        margin-top: 8px;
                    }
                    
                    .brand-name {
                        font-size: 20px;
                    }
                    
                    .logo-wrapper {
                        width: 38px;
                        height: 38px;
                    }
                    
                    .navbar-logo, .logo-fallback {
                        width: 38px;
                        height: 38px;
                        font-size: 20px;
                    }
                }
                
                /* Animation for mobile menu */
                .navbar-collapse {
                    transition: all 0.3s ease;
                }
                
                /* Active link style */
                .nav-link-custom.active {
                    background: rgba(249, 168, 37, 0.15);
                    color: #F9A825 !important;
                }
            `}</style>
        </BsNavbar>
    );
};

export default Navigation;